import { NgService } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { IRuleService } from "@app/services/rule.service";
import { Configurator, eFieldType, eRuleType, Field, IAddModelClickHandlerArgs, IAnimateCameraArgs, IAnimateClipArgs, IClaraSceneRuleArgs, IConstrainCameraArgs, IDraggable, IFrameSceneArgs, IGetNodeArgs, IGetTableRuleArgs, IGetUploadImageNodeArgs, IGhostArgs, IHighlightArgs, IJsResult, ILastValueCache, IPathCache, ISceneAnimationArgs, ISceneApi, ISceneCache, ISceneGetIdArgs, ISceneGetNameArgs, ISceneGetPropertyArgs, ISceneNode, ISceneRuleArgs, ISceneSetPropertyArgs, ISceneSetVisibleArgs, ISceneUtilities, ISelectNodeArgs, IToggleToolArgs, IUploadValue, KbObjectManager } from "@models";
import { ClientLogger, KPromise } from "@rules";
import { Color, Logger, Utils } from "@tools";

export enum eClaraPropertyType {
    string = "string",
    number = "number",
    boolean = "boolean",
    color = "color",
    reference = "reference"
}

export enum eHighlightType {
    user = "user",
    hover = "hover",
    selection = "selection"
}

export class ClaraSceneUtilities implements ISceneUtilities {
    constructor(
        public sceneService: ClaraSceneService,
        public sceneApi: ISceneApi,
        public ruleType: eRuleType,
        public idMap?: {},
        public nestedSceneId?: string
    ) {

    }
    public setProperty(args: ISceneSetPropertyArgs) {
        args.nestedSceneId = this.nestedSceneId;
        args.objectId = this.mapId(args.objectId);
        args.value = this.mapId(args.value); // sometimes the value is an id as well (like setting a material reference)
        this.sceneService.setProperty(this.sceneApi, args);
        if (this.sceneApi.afterSetProperty) this.sceneApi.afterSetProperty(args);
    }
    public getProperty(args: ISceneGetPropertyArgs) {
        args.nestedSceneId = this.nestedSceneId;
        args.objectId = this.mapId(args.objectId);
        return this.sceneService.getProperty(this.sceneApi, args);
    }
    public setVisible(args: ISceneSetVisibleArgs) {
        args.nestedSceneId = this.nestedSceneId;
        args.objectId = this.mapId(args.objectId);
        return this.sceneService.setVisibleRecursively(this.sceneApi, args);
    }
    public frameScene(args?: IFrameSceneArgs) {
        return this.sceneService.frameScene(this.sceneApi, args);
    }
    public animateCamera(args: IAnimateCameraArgs) {
        return this.sceneService.animateCamera(this.sceneApi, args);
    }
    public animate(args: ISceneAnimationArgs) {
        args.objectId = this.mapId(args.objectId);
        this.sceneService.animate(this.sceneApi, args);
        if (this.sceneApi.afterAnimateProperty) this.sceneApi.afterAnimateProperty(args);
    }
    public animateClip(args: IAnimateClipArgs) {
        return this.sceneService.animateClip(this.sceneApi, args);
    }
    public addModelClickHandler(args: IAddModelClickHandlerArgs) {
        args.modelId = this.mapId(args.modelId);        
        args.addedByRuleType = this.ruleType;

        // we need a unique id for the handler, but in the context of the nested configurator.
        if (this.nestedSceneId) {
            args.uniqueId = this.nestedSceneId + "-" + args.uniqueId;
            if (args.hotspotId) {
                args.hotspotId = this.nestedSceneId + "-" + args.hotspotId;
            }
        }
        if (this.sceneApi.addModelClickHandler) {
            return this.sceneApi.addModelClickHandler(args);
        }
    }
    public clearClickHandlers() {
        if (this.sceneApi.clickDb) this.sceneApi.clearClickHandlers();
    }
    public addDraggable(args: IDraggable) {
        args.addedByRuleType = this.ruleType;
        args.objectId = this.mapId(args.objectId);
        this.sceneApi.addDraggable(args);
    }
    public clearDraggables() {
        if (this.sceneApi.draggableDb) this.sceneApi.clearDraggables();
    }
    public ghost(args: IGhostArgs) {
        return this.sceneService.ghost(this.sceneApi, args);
    }
    public clearGhosts() {
        return this.sceneService.clearGhosts(this.sceneApi);
    }
    public highlight(args: IHighlightArgs) {
        return this.sceneService.highlight(this.sceneApi, args, eHighlightType.user);
    }
    public clearHighlights() {
        return this.sceneService.clearHighlightsOfType(this.sceneApi, eHighlightType.user);
    }
    public select(args: ISelectNodeArgs) {
        return this.sceneService.select(this.sceneApi, args);
    }
    public clearSelection() {
        return this.sceneService.clearSelection(this.sceneApi);
    }
    public getId(args: ISceneGetIdArgs): string {
        return this.sceneApi.cache.paths.getId({ name: args.name });
    }

    public getName(args: ISceneGetNameArgs): string {
        return this.sceneApi.api.scene.get({ id: args.id, property: "name" });
    }

    protected mapId(id): string {
        return this.idMap ? this.idMap[id] || id : id;
    }

    public getNode(args: IGetNodeArgs): ISceneNode {
        let o = this.sceneService.getQueryObject(this.sceneApi, args.id, null);
        let id = this.sceneApi.cache.paths.getId(o);
        let name = this.sceneApi.api.scene.get({ id, property: "name" });
        let t = this.sceneApi.api.scene.get({ id, property: "type" });

        return {
            id,
            name,
            type: t
        } as ISceneNode;
    }

    public getChildNodes(args: IGetNodeArgs): ISceneNode[] {
        let children: ISceneNode[] = [];
        let o = this.sceneService.getQueryObject(this.sceneApi, args.id, null);
        let id = this.sceneApi.cache.paths.getId(o);
        if (id) {
            // TODO: 2018-01-19 (RH): Had to cast this to <any> due to a type mismatch error in the
            // new TS 2.5 compiler.
            let childIds: any[] = this.sceneApi.api.scene.filter({ from: id as any } as any) as any;
            if (childIds && childIds.length) {
                childIds.forEach((childId: string) => {
                    children.push(this.getNode({ id: childId }));
                });
            }
        }
        return children;
    }

    public whenAssetsLoaded(): Q.IPromise<any> {
        return this.sceneApi.api.sceneIO.whenLoaded();
    }

    public getActiveCamera(): string{
        return this.sceneApi.api.player.getCamera();
    }
}

/**
 * PathCache stores a map between query objects and paths for the V2 player.  
 * Paths are better because it doesn't result in a search.
 * We also store the last set value so we don't keep setting the current value 
 * over and over which costs with performance.
 */
export class PathCache implements IPathCache {
    constructor(public sceneApi: ISceneApi) { }

    private _data = {};
    public get(query: clara2.IQueryObject): any[] {
        var paths = this.getPaths(query, false) as any[][];
        return paths.length ? paths.first() : [];
    }
    public getId(query: clara2.IQueryObject): string {
        var paths = this.getPaths(query, false) as string[];
        return paths.length ? paths.first() : null;
    }
    public getIds(query: clara2.IQueryObject): string[] {
        return this.getPaths(query, true) as string[];
    }
    public getAll(query: clara2.IQueryObject): any[][] {
        return this.getPaths(query, true) as any[][];
    }
    public clear() {
        this._data = {};
    }
    public clearEntriesThatContain(...searchTerms: string[]) {
        let keys = Object.keys(this._data);
        for (let key of keys) {
            for(let term of searchTerms){
                if (key.indexOf(term) > -1) {
                    delete this._data[key];
                    break;
                }
            }
        }
    }

    private getPaths(query: clara2.IQueryObject, forSetAll: boolean): any[][] | string[] {
        let key = JSON.stringify(query);
        let exists = this._data.hasOwnProperty(key);
        if (exists) {
            return this._data[key];
        } else {
            let paths: any[][] = [];
            if (forSetAll) {
                paths = this.sceneApi.api.scene.filter(query) as any[][];
            } else {
                let path = this.sceneApi.api.scene.find(query) as any[];
                if (path) paths.push(path);
            }
            if (!paths.length) {
                console.warn("cannot find object with query: " + JSON.stringify(query));
            } else {
                this._data[key] = paths;
            }
            return paths;
        }
    }
}

export class LastValueCache implements ILastValueCache {
    private _data = {};
    public get(path: any[]): any {
        let key = JSON.stringify(path);
        return this._data[key];
    }
    public set(path: any[], value: any) {
        this._data[JSON.stringify(path)] = JSON.stringify(value);
    }
    public valueEqualTo(path: any[], val: any) {
        let key = JSON.stringify(path);
        return this._data[key] === JSON.stringify(val);
    }
}

export class SceneCache implements ISceneCache {
    constructor(public sceneApi: ISceneApi) { }

    public paths = new PathCache(this.sceneApi);
    public lastValues = new LastValueCache();
}

@NgService({
    token: Token.ClaraSceneService,
    dependencies: [
        Token.RuleService,
        Token.$Q,
    ]
})
export class ClaraSceneService {
    constructor(
        private ruleService: IRuleService,
        private $q: ng.IQService
    ) {

    }

    private _vectorProperties: string[] = [
        "translationx",
        "translationy",
        "translationz",
        "rotationx",
        "rotationy",
        "rotationz",
        "scalex",
        "scaley",
        "scalez"
    ];

    public NESTED_SCENE_PREFIX = "kb-nested-";

    public convertColorForScene(hex: string): { r: number, g: number, b: number } {
        // clara expects rgb values from 0 to 1
        let color = new Color(hex).toRgb();
        color.r = color.r / 255;
        color.g = color.g / 255;
        color.b = color.b / 255;
        return color;
    }

    private normalizePropertyValue(sceneApi: ISceneApi, args: ISceneSetPropertyArgs) {
        let origValue: any = args.value;
        if (args.propertyType && args.propertyType.isEqual("Color")) { // property type is the clara data type, not the snap data type
            // if value is already an rgb object, then skip this
            if (args.value &&
                typeof args.value === "object" &&
                args.value.hasOwnProperty("r") &&
                args.value.hasOwnProperty("g") &&
                args.value.hasOwnProperty("b")
            ) {
                // do nothing
            } else if (args.value !== null) {
                args.value = this.convertColorForScene(args.value);
            }
        }
        if (this._vectorProperties.indexOf(args.property) > -1) {
            let axis = args.property.charAt(args.property.length - 1);
            args.property = args.property.substr(0, args.property.length - 1);

            let currentVal = this.getProperty(sceneApi, {
                objectId: args.objectId,
                name: args.name,
                plug: args.plug,
                operator: args.operator,
                property: args.property,
                propertyType: null,
                nestedSceneId: args.nestedSceneId
            });

            if (currentVal && currentVal.clone) currentVal = currentVal.clone();
            currentVal[axis] = origValue;

            args.value = currentVal;
        }
    }

    public toggleTool(sceneApi: ISceneApi, args: IToggleToolArgs) {
        sceneApi.api.commands.updateCommand(args.tool, { enabled: args.enabled });
    }

    public constrainCamera(sceneApi: ISceneApi, args: IConstrainCameraArgs) {
        let radius: any = args.constrain ? args.radius : null;
        sceneApi.api.player.setCameraRadiusConstraint(radius);
    }

    /**
     * validates that the given string is a valid clara id.  Note that clara used to use guids, 
     * and now uses these: https://github.com/ericelliott/cuid
     * so we need to validate for both
     * @param id
     */
    public isClaraId(id: string): boolean {
        let cuidRegex = /^c[^\s-]{8,}$/;
        return Utils.isGuid(id) || (cuidRegex.exec(id) != null);
    }

    public getParentNodeId(sceneApi: ISceneApi, nodeId: string) {
        return sceneApi.api.scene.find({
            id: nodeId,
            parent: true
        } as clara2.IQueryObject) as string;
    }

    public getChildNodeIds(sceneApi: ISceneApi, nodeId: string): string[] {
        return sceneApi.api.scene.filter({ from: nodeId }) as string[];
    }

    public findAncestorNodeId(sceneApi: ISceneApi, nodeId: string, callback: (nodeId: string) => boolean): string {
        let parentId = nodeId;
        while (parentId) {
            if (callback(parentId)) break;
            parentId = this.getParentNodeId(sceneApi, parentId);
        }
        return parentId;
    }

    public getProperty(sceneApi: ISceneApi, args: ISceneGetPropertyArgs) {
        let axis: string;
        if (this._vectorProperties.indexOf(args.property) > -1) {
            axis = args.property.charAt(args.property.length - 1);
            args.property = args.property.substr(0, args.property.length - 1);
        }

        let query: clara2.IQueryObject = this.getQueryObjectFromArgs(sceneApi, args);
        // if the item is in a nested scene, we must look for the item only in the nested scene null,
        // otherwise regex style sets will be applied to the whole scene
        // materials are not nested under the nested scene null so they do not apply & 
        // from is not needed if doing an id search, so we only use it when doing by name
        if (args.nestedSceneId && query.name && !args.operator.equalsAny("Physical", "Canvas", "CanvasText")) {
            query.from = { id: args.nestedSceneId };
        }
        
        let path = sceneApi.cache.paths.get(query);
        let result = null;
        result = sceneApi.api.scene.get(path);

        if (axis && result) {
            result = result[axis];
        }
        return result;
    }

    public setProperty(sceneApi: ISceneApi, args: ISceneSetPropertyArgs) {
        this.normalizePropertyValue(sceneApi, args);

        // if this is a reference type, then we need to set the reference property to the id of the referenced item
        if (args.propertyType && args.propertyType.isEqual("Node")) {// property type is the clara data type, not the snap data type
            if (!this.isClaraId(args.value)) {
                args.value = sceneApi.cache.paths.getId({ name: args.value });
            }
        }

        let query: clara2.IQueryObject = this.getQueryObjectFromArgs(sceneApi, args);
        // if the item is in a nested scene, we must look for the item only in the nested scene null,
        // otherwise regex style sets will be applied to the whole scene
        // materials are not nested under the nested scene null so they do not apply & 
        // from is not needed if doing an id search, so we only use it when doing by name
        if (args.nestedSceneId && query.name && !args.operator.equalsAny("Physical", "Canvas", "CanvasText")) {
            query.from = { id: args.nestedSceneId };
        }

        let setAll: boolean = (query.name != null); // we use setAll if this is possibly a regex query
        let paths = sceneApi.cache.paths.getAll(query);
        paths.forEach(path => {
            if (!sceneApi.cache.lastValues.valueEqualTo(path, args.value)) {
                sceneApi.api.scene.set(path, args.value);
                Logger.logSceneChange("set " + JSON.stringify(args));
                sceneApi.cache.lastValues.set(path, args.value);
            }
        });
    }

    public setVisibleRecursively(sceneApi: ISceneApi, args: ISceneSetVisibleArgs) {

        // hierarchical visibility is not set, so we fallback to what we used to do, which is setting the node and all it's children's visibility to simulate hierarchical visibility
        let query: clara2.IQueryObject = { name: "*", plug: "Properties", property: "visible" };
        query.includeParent = true; // include the parent in the query

        query.from = this.getQueryObject(sceneApi, args.objectId, args.name);
        if (!query.from) return;

        // if we're in a nested scene, then only look for nodes inside of that
        if (args.nestedSceneId) {
            (query.from as clara2.IQueryObject).from = { id: args.nestedSceneId };
        }

        let paths = sceneApi.cache.paths.getAll(query);
        if (paths) {
            paths.forEach(path => {
                if (!sceneApi.cache.lastValues.valueEqualTo(path, args.visible)) {
                    sceneApi.api.scene.set(path, args.visible);
                    Logger.logSceneChange("set " + JSON.stringify(args));
                    sceneApi.cache.lastValues.set(path, args.visible);
                }
            });
        }
    }

    private getQueryObjectFromArgs(sceneApi: ISceneApi, args: ISceneGetPropertyArgs): clara2.IQueryObject {
        return this.getQueryObject(sceneApi, args.objectId, args.name, args.plug, args.property, args.operator);
    }

    public getQueryObject(
        sceneApi: ISceneApi,
        id: string,
        name: string,
        plug?: string,
        property?: string,
        operator?: string
    ): clara2.IQueryObject {
        let o: clara2.IQueryObject = {};

        // temporary check for transition to V2.  Old blocks will only be using objectId even if it's the name 
        // we're after newer blocks differentiate.  So we need to make sure the id is really an id.  
        // If it isn't, then we'll use it as the name
        if (id) {
            let foundId = sceneApi.api.scene.find({ id });
            if (foundId) {
                o.id = id;
            } else {
                o.name = id;
            }
        } else if (name) {
            o.name = name;
        }
        if (plug) {
            o.plug = plug;
        }
        if (property) {
            o.property = property;
        }
        if (operator) {
            // using named operators is a little strange.  We need to match the operator through it's properties.  
            // In this case, we use the name property
            o.properties = { name: operator };
        }

        return o;
    }

    public animateCamera(sceneApi: ISceneApi, args: IAnimateCameraArgs) {
        let duration = args.duration || 3000;
        let easing = args.easing || "linear";


        sceneApi.cameraAnimationInProgress = true;
        return sceneApi.api.player.animateCameraTo(args.toCameraId, duration).then(() => {
            sceneApi.cameraAnimationInProgress = false;
        });

    }

    public animate(sceneApi: ISceneApi, args: ISceneAnimationArgs) {
        this.normalizePropertyValue(sceneApi, args);


        let query = this.getQueryObjectFromArgs(sceneApi, args);
        let setAll: boolean = (query.name != null); // we use setAll if this is possibly a regex query
        let paths = sceneApi.cache.paths.getAll(query);
        paths.forEach(path => {
            // if (!sceneApi.cache.lastValues.valueEqualTo(path, args.value)) {
            sceneApi.api.animation.queueAnimation({
                name: Utils.shortId(),
                iterations: args.iterations,
                autoplay: true,
                tracks: [
                    {
                        path: path as any,
                        type: args.propertyType,
                        value: args.value,
                        duration: args.duration,
                        easing: args.easing || "linear",
                        start: args.start
                    }
                ],
                onEnd: () => {
                    // after the animation set the value in the cache
                    sceneApi.cache.lastValues.set(path, args.value);
                }
            });
            // }
        });
    }

    public animateClip(sceneApi: ISceneApi, args: IAnimateClipArgs) {
        let clip = sceneApi.api.animation.getClips().find(c => c.name.isEqual(args.name));
        if (clip) {
            sceneApi.api.animation.queueClip(clip.id, { autoplay: true });
        } else {
            console.warn("Could not find animation clip with name '" + args.name + "'");
        }
    }

    public getSceneUtilities(
        sceneApi: ISceneApi,
        ruleType: eRuleType,
        idMap?: {},
        nestedSceneId?: string) {
        return new ClaraSceneUtilities(this, sceneApi, ruleType, idMap, nestedSceneId);
    }

    protected runSceneRules(sceneConfig: Configurator,
        uiConfig: Configurator,
        ruleArgs: IClaraSceneRuleArgs,
        skipRulesIfNoNestedSceneChanges: boolean
    ): ng.IPromise<IJsResult<ISceneRuleArgs>> {
        return this.runNestedSceneRules(ruleArgs.sceneApi, uiConfig).then((r: any[]) => {
            if (!skipRulesIfNoNestedSceneChanges || (r && r.some(r2 => r2 != null))) { //skip running the rules if there were no nested scene rules run
                return this.importAllUploadFieldImages(ruleArgs.sceneApi, uiConfig).then(() => {
                    ruleArgs.sceneUtils.ruleType = eRuleType.scene;
                    return this.ruleService.runRuleTypeAsync(sceneConfig, eRuleType.scene, ruleArgs).then(ruleResult => {
                        uiConfig.$fieldsDirtyForSceneRules = false;
                        return ruleResult;
                    });
                });
            }
        });
    }

    public runRulesCycle(
        sceneConfig: Configurator,
        uiConfig: Configurator,
        ruleArgs: IClaraSceneRuleArgs
    ): ng.IPromise<IJsResult<ISceneRuleArgs>> {

        let runUiRules = !ruleArgs.sceneApi.sceneDb[sceneConfig.idScene].idProduct;

        // before running the scene rules, we need to clear out transient handlers
        ruleArgs.sceneApi.draggableDb.clearTransient();
        ruleArgs.sceneApi.clickDb.clearTransient();

        if (runUiRules) {
            // run the value rule first
            ruleArgs.sceneUtils.ruleType = eRuleType.value;
            return this.ruleService.runRuleTypeAsync(sceneConfig, eRuleType.value, ruleArgs)
                .then(() => {
                    sceneConfig.preValidate();
                    sceneConfig.isValid();
                })
                .then(() => this.runSceneRules(sceneConfig, uiConfig, ruleArgs, false));
        } else {
            return this.runSceneRules(sceneConfig, uiConfig, ruleArgs, false);
        }
    }

    public runNestedSceneRules(
        sceneApi: ISceneApi,
        uiConfig: Configurator
    ) {

        let nestedConfigs = uiConfig.getNestedConfigurators();

        if (nestedConfigs.length) {
            let promises: ng.IPromise<any>[] = [];
            // loop through nested configurators, merge the scenes if they haven't been already, and run their rules
            nestedConfigs.forEach(nested => {
                if (nested.idScene) {
                    let refConfig = uiConfig.referencedConfigurators.find(rc => rc.idProduct == nested.idProduct);
                    if (refConfig.nestScene) {
                        // create the scene configurator for the nested configurator if it doesn't already exist 
                        // (or has been swapped for another scene)
                        if (!nested.$sceneConfigurator || nested.$sceneConfigurator.idScene != nested.idScene) {
                            nested.$sceneConfigurator = new Configurator(
                                new KbObjectManager(),
                                sceneApi.sceneDb[nested.idScene].configurator
                            );
                            nested.$fieldsDirtyForSceneRules = true;
                        }
                        nested.$sceneConfigurator.$uiConfigurator = nested;
                        nested.$nestHotspots = refConfig.nestHotspots;

                        // in case the nested scene is a standalone scene, we need to set it's fields from the uiConfig
                        if (nested != nested.$sceneConfigurator && !nested.$sceneConfigurator.idProduct) {
                            nested.$sceneConfigurator.setFields(nested.getFieldsObject());
                        }

                        let skipRules = !nested.$fieldsDirtyForSceneRules;
                            
                        promises.push(
                            this.nestScene( // merge the scene if it hasn't already been merged
                                sceneApi,
                                sceneApi.sceneDb[nested.idScene].claraId,
                                sceneApi.sceneDb[nested.idScene].publishHash,
                                nested
                            ).then(() =>
                                // now run the rules on the nested scene
                                this.runSceneRules(
                                    nested.$sceneConfigurator,
                                    nested,
                                    this.getNestedSceneRuleArgs(
                                        sceneApi,
                                        nested,
                                        nested.$sceneConfigurator,
                                        nested.$sceneConfigurator.$sceneIdMap,
                                        eRuleType.scene
                                    ),
                                    skipRules
                                )
                            )
                        );
                    }
                }
            });
            return this.$q.all(promises);
        } else {
            return (new KPromise(null)) as any;
        }

    }

    public getNestedSceneRuleArgs(sceneApi: ISceneApi, uiConfig: Configurator, sceneConfig: Configurator, idMap: {}, ruleType: eRuleType) {
        // if the scene config is standalone, then it will also act as the uiConfig for the rules
        if (!sceneApi.sceneDb[uiConfig.idScene].idProduct) uiConfig = uiConfig.$sceneConfigurator;
        let sceneUtils = this.getSceneUtilities(sceneApi, ruleType, idMap, uiConfig.$nestedSceneId);

        return {
            environment: "",
            company: {},
            configurator: uiConfig,
            kom: uiConfig.$manager,
            parentKom: uiConfig.$parentConfigurator ? uiConfig.$parentConfigurator.$manager : null,
            sceneKom: sceneConfig.$manager,
            sceneApi,
            isRender: sceneApi.isRender,
            isMobile: sceneApi.isMobile,
            clientLanguage: sceneApi.clientLanguage,
            logs: new ClientLogger(),
            upload: () => {
                return new KPromise(null) as any;
            },
            // TODO:  leave in scene rule args for backwards compatibility.  
            // Can be removed in the future, since it's real place is in scene utilities
            addModelClickHandler: args => {
                sceneUtils.addModelClickHandler(args);
            },
            playSceneAnimation: args => {
                // no need for animations server side
            },
            toggleSceneAnimation: () => {
                // no need for animations server side
            },
            sceneUtils,
            sendMessage: msg => {
                sceneApi.sendMessage(msg);
            },
            selectPage: page => {

            },
            convertCurrency: (obj: any) => {
                return null;
            },
            toggleTool: (args: IToggleToolArgs) => {

            },
            constrainCamera: (args: IConstrainCameraArgs) => {

            },
            getUploadImageNode: (args: IGetUploadImageNodeArgs) => {
                let a = sceneApi.importedImages[uiConfig.name + "_" + args.fieldId];
                return a ? a.imageNodeId : null;
            },
            getTables: (args: IGetTableRuleArgs) => {
                return sceneApi.getTables(args);
            }
        } as IClaraSceneRuleArgs;
    }

    public nodeWithIdExists(sceneApi: ISceneApi, id: string): boolean {
        return id && (sceneApi.api.scene.find({ id }) != null);
    }

    public nestSceneInner() {

    }

    public fetchScene(sceneApi: ISceneApi, claraId: string, publishHash: string): Q.IPromise<any> {
        if (!sceneApi.fetchedScenes.hasOwnProperty(claraId)) {
            sceneApi.fetchedScenes[claraId] =
                sceneApi.api.sceneIO.fetch(claraId, publishHash, { waitForPublish: true });
        }
        return sceneApi.fetchedScenes[claraId];
    }

    protected nestScene(
        sceneApi: ISceneApi,
        claraId: string,
        publishHash: string,
        nested: Configurator
    ): ng.IPromise<any> {
        // first check to see if this nested configurator scene has already been merged
        if (nested.$nestedSceneId && this.nodeWithIdExists(sceneApi, nested.$nestedSceneId)) {
            return (new KPromise(null)) as any;
        } else {
            let deferred = this.$q.defer();

            let api = sceneApi.api;
            let rootSceneId = api.scene.find({ includeParent: true }) as string;
            // first fetch the scene from the server (or cache)
            this.fetchScene(sceneApi, claraId, publishHash).then(() => {

                // if this nested scene is a child of another nested scene, 
                // then we must place this node inside of the parent nested scene node
                // otherwise, just put it in the 'Objects' node of the scene
                let parentId: string;
                if (nested.$parentConfigurator && nested.$parentConfigurator.$nestedSceneId) {
                    parentId = nested.$parentConfigurator.$nestedSceneId;
                } else {
                    parentId = sceneApi.cache.paths.getId({ type: "Objects" }) as string; // get the 'Objects' node id
                }

                // now that we've fetched the nested scene template, we need to add a null node to house it
                api.sceneGraph.addNode({
                    name: this.NESTED_SCENE_PREFIX + nested.name,
                    parent: parentId,
                    type: "Null",
                    plugs: {
                        Transform: [["Transform", {}]],
                        Properties: [["Default", {}]]
                    }
                }).then(nullId => {
                    // we have our new null node to house the nested scene.  
                    // Now we need to copy nodes from the other scene into it
                    let nestedObjectsNodeId = sceneApi.cache.paths.getId({
                        from: { id: claraId },
                        name: "Objects"
                    }) as string;
                    let nestedMaterialsNodeId = sceneApi.cache.paths.getId({
                        from: { id: claraId },
                        name: "Material Library"
                    }) as string;
                    let parentMaterialsNodeId = sceneApi.cache.paths.getId({
                        from: { id: rootSceneId },
                        name: "Material Library"
                    }) as string;

                    // only clone nodes that make sense - materials can stay where they're 
                    // at and still be used.Ignore cameras, lights, etc.
                    let objectsToClone = api.scene.filter({
                        from: { id: nestedObjectsNodeId },
                        type: ["PolyMesh", "BinMesh", "Null", "Model", "Annotation", "Helper"]
                    }) as string[];
                    let materialsToClone = api.scene.filter({
                        from: { id: nestedMaterialsNodeId },
                        type: ["Material"]
                    }) as string[];
                    let allNodesToClone = objectsToClone.concat(materialsToClone);

                    // create a parent map to tell the cloned nodes where to go
                    let parentMap = {};
                    // nested object nodes to go into our new null
                    parentMap[nestedObjectsNodeId] = nullId;
                    // nested material nodes to go into the parent material library
                    parentMap[nestedMaterialsNodeId] = parentMaterialsNodeId;

                    // this.dumpScene(sceneApi, <string>api.scene.find({ includeParent: true }), 0);
                    api.sceneGraph.clone(allNodesToClone, parentMap, { cloneDependencies: true }).then(nodeMap => {
                        // console.log('new nodes', nodeMap);
                        let sceneId = api.scene.find({ includeParent: true }) as string;
                        // this.dumpScene(sceneApi, sceneId, 0);
                        // this.checkScene(sceneApi, sceneId, 0, sceneId);
                       
                        // store the node map into the scene configurator
                        nested.$sceneConfigurator.$sceneIdMap = nodeMap;
                        // set the nested scene id of the configurator to make it easier to find this node by id
                        nested.$nestedSceneId = nullId;

                        if (sceneApi.onSceneNested) {
                            sceneApi.onSceneNested(nested, nullId);
                        }

                        let nestedRuleArgs = this.getNestedSceneRuleArgs(
                            sceneApi,
                            nested,
                            nested.$sceneConfigurator,
                            nested.$sceneConfigurator.$sceneIdMap,
                            eRuleType.sceneLoaded
                        );
                        this.ruleService.runRuleTypeAsync(nested.$sceneConfigurator, eRuleType.sceneLoaded, nestedRuleArgs).then(() => {
                            deferred.resolve();
                        });
                    });
                });
            });
            return deferred.promise;
        }
    }

    public deleteOrphanedNestedScenes(sceneApi: ISceneApi, uiConfig: Configurator) {
        let allNested = uiConfig.getAllConfigurators();

        let api = sceneApi.api;
        let nestedSceneIds = api.scene.filter({ name: this.NESTED_SCENE_PREFIX + "*" }) as string[];
        nestedSceneIds.forEach(id => {
            if (!allNested.some(c => c.$nestedSceneId == id)) {
                api.sceneGraph.deleteNode(id);
            }
        });

    }

    /**
     * used to output the heirarchy for debugging
     * @param level
     */
    public dumpScene(sceneApi: ISceneApi, nodeId: string, level: number) {
        let api = sceneApi.api;
        if (nodeId == null) {
            nodeId = sceneApi.api.scene.find({ includeParent: true }) as string;
            level = 0;
        }
        // tslint:disable-next-line:no-console
        console.log(new Array(level + 1).join("  "), " - ", api.scene.get({ id: nodeId, property: "name" }), " - ", nodeId);
        let paths = api.scene.filter({ from: { id: nodeId }, shallow: true }) as string[];
        paths.forEach(id => this.dumpScene(sceneApi, id, level + 1));
    }

    public checkScene(sceneApi: ISceneApi, nodeId: string, level, parentId) {
        let api = sceneApi.api;
        let name = api.scene.get({ id: nodeId, property: "name" });
        if (!name) {
            console.error(
                "No data for child: ", nodeId, "child of: ", api.scene.get({ id: parentId, property: "name" })
            );
        }
        let children = api._store.getIn(["sceneGraph", nodeId, "children"]);
        children.forEach(id => this.checkScene(sceneApi, id, level + 1, nodeId));
    }

    public importAllUploadFieldImages(sceneApi: ISceneApi, uiConfig: Configurator): ng.IPromise<any> {
        let promise = new KPromise(null) as any;

        // only find fields with an assetId but don't have an image node yet.  
        // The asset should have already been imported at time of upload
        // (has to be this way because we don't have the File object)
        let uploadFields = uiConfig.getFields(f =>
            f.type == eFieldType.upload &&
            f.uploadToScene
        );
        if (uploadFields.length) {
            let promises: ng.IPromise<any>[] = [];
            uploadFields.forEach(uf => {
                promises.push(this.importUploadFieldImage(sceneApi, uiConfig, uf));
            });
            promise = this.$q.all(promises);
        }

        return promise;
    }

    public importUploadFieldImage(sceneApi: ISceneApi, uiConfig: Configurator, field: Field, file?: File): ng.IPromise<any> {
        let promise = new KPromise(null) as PromiseLike<clara2.IImportImageResult>;
        let uploadValue = field.value as IUploadValue;
        let api = sceneApi.api;
        let isRender = sceneApi.isRender;

        // only import if the upload field is set to upload to scene
        if (field.uploadToScene) {  
            let prefix = uiConfig.isNested() ? uiConfig.name : "";
            prefix += "_" + field.name;
            let nullNodeName = prefix + "_null";
            let imageNodeName = prefix + "_image";
            let shapeNodeName = prefix + "_shape";
            let extrudeNodeName = prefix + "_extrude";
            let options: clara2.IImageImportOptions = {outputName: imageNodeName};

            if (field.convertUploadedSceneImageToMesh) {
                options = {
                    targetFormat: "svg",
                    mode: "threshold",
                    threshold: field.uploadImageThreshold,
                    backgroundColor: "#00FFFFFF",
                    outputName: imageNodeName
                };
            }

            // if there is already an asset id, and it has an image node, then there is nothing to do here
            // if there is no asset id, then this file still needs to get uploaded to assets
            let importObject = null;
            let prevImportedImage = sceneApi.importedImages[uiConfig.name + "_" + field.id];

            if (file && !uploadValue.assetId) { //user just uploaded                
                importObject = file;
            } else if (uploadValue.assetId && !prevImportedImage) { //uploaded this session (and not an svg conversion)
                // pass the assetId to import to make the image node
                importObject = uploadValue.assetId;
            }

            if (importObject) {
                promise = sceneApi.api.assets.importImage(importObject, options);
            } else {
                return promise as any;
            }

            //clean up old svg image if it exists
            let svgNodeId = api.scene.find(imageNodeName + ".svg") as string;
            if (svgNodeId) {
                api.sceneGraph.deleteNode(svgNodeId);
            }
            promise.then(r => {
                uploadValue.assetId = r.assetId;
                sceneApi.importedImages[uiConfig.name + "_" + field.id] = { imageNodeId: r.imageNodeId, threshold: field.uploadImageThreshold };
                
                if (field.convertUploadedSceneImageToMesh) {
                    //create a null to house the shape if one doesn't already exist
                    let nullNodeId = api.scene.find(nullNodeName) as string;
                    if (!nullNodeId) {
                        api.scene.addNode({
                            name: nullNodeName,
                            parent: api.scene.find('Objects') as string,
                            type: "Null",
                            plugs: {
                                Transform: [["Transform", {}]],
                                Properties: [["Default", {}]]
                            }
                        });
                        nullNodeId = api.scene.find(nullNodeName) as string;
                    }

                    //if the shape node already exists, then delete it
                    //if it doesn't exist, then create it
                    let shapeNodeId = api.scene.find(shapeNodeName) as string;
                    if (shapeNodeId) {
                        api.sceneGraph.deleteNode(shapeNodeId);
                        sceneApi.cache.paths.clearEntriesThatContain(shapeNodeName, shapeNodeId);
                    }
                    api.scene.addNode({
                        name: shapeNodeName,
                        type: 'Shape',
                        parent: nullNodeId,
                        plugs: {
                            Shape: [
                                [
                                    'SVGShape',
                                    {
                                        image: r.imageNodeId,
                                        curveSegments: 8,
                                    },
                                ],
                            ]
                        },
                    });
                    
                    shapeNodeId = api.scene.find(shapeNodeName) as string;

                    // Resize shape (default operation is to fit a 1x1 square)
                    api.scene.addOperator(
                        shapeNodeId,
                        'Shape',
                        'ResizeShape',
                        { keepAspectRatio: true }
                    );

                    // Center the shape since center of svg is on top left corner
                    api.scene.addOperator(
                        shapeNodeId,
                        'Shape',
                        'CenterShape',
                        {}
                    );

                    //add an extrude of the shape
                    let extrudeNodeId = api.scene.find(extrudeNodeName) as string;
                    if (extrudeNodeId) {
                        api.sceneGraph.deleteNode(extrudeNodeId);
                        sceneApi.cache.paths.clearEntriesThatContain(extrudeNodeName, extrudeNodeId);
                    }
                    api.scene.addNode({
                        name: extrudeNodeName,
                        type: "PolyMesh",
                        parent: nullNodeId,
                        plugs: {
                            PolyMesh: [
                                [
                                    "FromShapeWithExtrude",
                                    {
                                        shape: shapeNodeId,
                                        extrudeLength: 0.1
                                    }
                                ]
                            ]
                        }
                    });
                }
            });
        }
        return promise as ng.IPromise<any>;
    }

    public snapshot(sceneApi: ISceneApi, options: clara2.ISnapshotOptions): Blob | string {
        sceneApi.api.commands.setCommandOptions('snapshot', options);
        var result = sceneApi.api.commands.runCommand('snapshot');
        return result;
    }

    public getRootSceneId(sceneApi?: ISceneApi) {
        return sceneApi.api.scene.find({ includeParent: true }) as string;
    }

    public frameScene(sceneApi: ISceneApi, args?: IFrameSceneArgs) {
        var nodeList = args ? [args.node] : null;
        sceneApi.api.player.frameScene(nodeList, args);
    }

    public isHierarchicalVisibilityEnabled(sceneApi: ISceneApi): boolean {
        let rootSceneId = sceneApi.api.scene.find({ includeParent: true }) as string;
        let r = sceneApi.api.scene.get({
            id: rootSceneId,
            plug: "Properties",
            property: "hierarchyVisibility"
        }) as string;
        return r == "Enable";
    }

    public ghost(sceneApi: ISceneApi, args: IGhostArgs) {
        if (!sceneApi.ghoster) {
            sceneApi.ghoster = sceneApi.api.selection.addGhoster({ opacity: .7 });
        }
        let nodeIds = sceneApi.api.scene.filter({ from: { id: args.id }, includeParent: true }) as string[];
        nodeIds.forEach(n => {
            if (args.on) {
                sceneApi.ghoster.selectionSet.add(n);
            } else {
                sceneApi.ghoster.selectionSet.remove(n);
            }
        });
    }

    public highlight(sceneApi: ISceneApi, args: IHighlightArgs, type: eHighlightType) {
        if (!sceneApi.highlighter) {
            sceneApi.highlighter = sceneApi.api.selection.setHighlighting(true, { color: "#ff0000", thickness: 2 });
        }

        let nodeIds = sceneApi.api.scene.filter({ from: { id: args.id }, includeParent: true, type: ["PolyMesh", "BinMesh", /*"Null",*/ "Model", "Annotation", "Helper"] }) as string[];
        let highlighter = sceneApi.highlighter;
        nodeIds.forEach(n => {
            if (args.on) {
                highlighter.selectionSet.add(n);
                sceneApi.highlights[type][n] = true;
            } else {
                highlighter.selectionSet.remove(n);
                delete sceneApi.highlights[type][n];
            }
        });
    }

    public clearHighlightsOfType(sceneApi: ISceneApi, type: eHighlightType) {
        let types = [eHighlightType.user, eHighlightType.selection, eHighlightType.hover];
        types.remove(type);
        for (let id in sceneApi.highlights[type]) {
            //only remove if the node is not part of another highlight type
            let shouldUnhighlight = !types.some(t => sceneApi.highlights[t][id]);

            if (shouldUnhighlight && sceneApi.highlighter) {
                sceneApi.highlighter.selectionSet.remove(id);
            }
        }
        sceneApi.highlights[type] = {};
    }

    public clearGhosts(sceneApi: ISceneApi) {
        if (sceneApi.ghoster) {
            sceneApi.ghoster.selectionSet.clear();
        }
    }

    public select(sceneApi: ISceneApi, args: ISelectNodeArgs) {
        if (!sceneApi.selector) {
            sceneApi.selector = sceneApi.api.selection.createSelectionSet();
            sceneApi.api.selection.setActiveSelectionSet(sceneApi.selector);
        }
        if (!sceneApi.selectedNodes.contains(args.id)) {
            this.clearSelection(sceneApi);

            sceneApi.selectedNodes.push(args.id);
            sceneApi.selector.add(args.id);

            this.highlight(sceneApi, { id: args.id, on: true }, eHighlightType.selection);
        }
    }

    public deselect(sceneApi: ISceneApi, nodeId: string) {
        this.highlight(sceneApi, { id: nodeId, on: false }, eHighlightType.selection);
        sceneApi.selectedNodes.remove(nodeId);
        sceneApi.selector && sceneApi.selector.remove(nodeId);
    }

    public clearSelection(sceneApi: ISceneApi) {
        for (let i = sceneApi.selectedNodes.length - 1; i >= 0; i--) {
            this.deselect(sceneApi, sceneApi.selectedNodes[i]);
        }
    }

    public getThree(sceneApi: ISceneApi): any {
        return sceneApi.api.player.getThree().THREE;
    }
}
